import axios from 'axios';
import { ref } from 'vue';


const ttsDomain = process.env.VUE_APP_TTS_ENDPOINT || 'https://unalengua.com';
const apiVersion = process.env.VUE_APP_TTS_API_VERSION || 'ttsv2';
const translateEndpoint = `${ttsDomain}/${apiVersion}`;
const voicesEndpoint = `${ttsDomain}/ttsVoices`;
const token = process.env.VUE_APP_DEV_TTS_TOKEN || navigator.userAgent;
export default class ServerSynthesis {

  constructor(language, voiceId){
    this.listeners = [];
    this.init(language, voiceId);
  }

  init(language, voiceId, currentTime){
    this.language = language;
    this.voiceId = voiceId;
    this.audios = [];
    this.audiosMaps = [];
    this.currentAudio;
    this.currentAudioMap;
    this.speed = 3;
    this.voice;
    this._loadVoices();
  }

  setSpeed(speed){
    this.speed = speed;
  }

  setVoice(voice){
    this.voice = voice
  }

  async getVoices(){
    return await this.voices;
  }

  async getVoice(){
    return this.voice || (await this.voices)[0];
  }

  async getAudioMap(){
    return this.currentAudioMap;
  }

  cancel(){
    if (this.currentAudio && !this.currentAudio.paused){
      this.currentAudio.pause();
    }
  }

  destroy(){
    this.listeners = {}; // Remove all stored listeners
  }

  addEventListener(eventType, listener){
    if (typeof listener !== 'function'){
      throw new Error('Second parameter must be a function');
    }
    this.listeners[eventType] ||= [];
    this.listeners[eventType].push(listener);
  }

  removeEventListener(eventType, listener){
    if (typeof listener !== 'function'){
      throw new Error('Second parameter must be a function');
    }
    this.listeners[eventType] ||= [];
    this.listeners[eventType] = this.listeners[eventType].filter(l => l !== listener);
  }


  notifyListeners(eventType, ...params){
    if (this.listeners[eventType]){
      this.listeners[eventType].forEach(listener => listener(...params))
    }
  }

  play(text, options = {}){
    // console.log(options);
    //const that = options.core;
    this.currentTime = ref(0);
    return new Promise((res, rej) => {
      this._getAudioFileLocation(text, options)
      .then(audioObj=>{
        this.cancel();
        this.currentAudio = audioObj;
        this.currentAudio.load();
        this.currentAudio.addEventListener('ended', () => {
          if(options.callback){
            options.callback();
          }
          res();
        });
        this.currentAudio.addEventListener('timeupdate', () => {
          this.currentTime.value = this.currentAudio.currentTime;
          this.notifyListeners('timeupdate');
        });
        this.currentAudio.addEventListener('canplaythrough', this._handlePlay.bind(this));
      })
      .catch(err => {
        rej(err);
      })
    })
  }

  prepareTexts(texts, options = []){
    // prefetch texts
    texts.forEach((text, i) => this._getAudioFileLocation(text, options[i]));
  }

  _handlePlay(){
    this.currentAudio.play();
  }

  async _getAudioFileLocation(text, options = {}){
    let ipa = ''
    if (options.ipa){
      ipa = options.ipa;
    }
    const textId = text + this.speed + this.voice.voiceURI + '';
    if (this.audios[textId]){
      return this.audios[textId];
    } else {
      const noWords = text.split(' ').length;
      this.audios[textId] = this._fetchAudioObj(text, options) 
      .then(async (response) => {
        const audioObj = new Audio(response.data.output_link);
        const res = await fetch(response.data.output_link);
        const blob = await res.blob();
        this.audiosMaps[textId] = noWords > 1 ? (await this._fetchAudioMapObj(text, textId, blob)) : null;
        this.currentAudioMap = this.audiosMaps[textId];
        return audioObj;
      });
      return this.audios[textId];
    }
  }

  async _fetchAudioMapObj(text, textId, blob) {
    let formData = new FormData();
    formData.append('file', blob);
    let result = await fetch(`${process.env.VUE_APP_WHISPER_URL}/whisper/timestamps/${this.language}/${text.replace(' ', '+')}`, {
      body: formData,
      method: "post"
    });
    let response = await result.json();
    return response.results[0].audio_map;
  }

  _fetchAudioObj(text, options){
    return axios.get(translateEndpoint, {
      headers: { 
        Authorization: `Bearer ${token}`
      },
      params: {
        text: text,
        speed: this.speed,
        voice: this.voice.voiceURI,
        ...options
      }
    })
    .catch(err=>{
      const message = (err.response && err.response.data && err.response.data.error) || err.message;
      throw new Error(message);
    })
  }

  async _loadVoices(){
    this.voices = ServerSynthesis.getVoices(this.language);
    const resolvedVoices = await this.voices;
    this.voice = resolvedVoices.find(voice => voice.voiceURI === this.voiceId) || resolvedVoices[0];
   }

  static async getVoices(language){
    return new Promise((res)=>{
      axios.get(`${voicesEndpoint}/${language}`, {
        headers: { 
          Authorization: `Bearer ${token}`
        }
      })  
      .then(resp => {
        const serverVoices = resp.data;
        const mappedVoices = serverVoices.map(serverVoice => ({
          voiceURI: serverVoice.Id,
          name: serverVoice.Name,
          lang: serverVoice.LanguageCode,
        }));
        res(mappedVoices)
      })
    })
  }

  static async isSupported(language){
    return (await ServerSynthesis.getVoices(language)).length > 0;
  }
}
